import { supabase } from '@/lib/supabase'
import type { MembresiaOrganizacion, Organizacion, RolOrganizacion } from '@/types'

type DbRow = Record<string, unknown>

interface OrganizationRepositoryClient {
  from: (table: 'organizations' | 'organization_members') => {
    select: (columns: string) => {
      order: (
        column: string,
        options: { ascending: boolean }
      ) => Promise<{ data: DbRow[] | null; error: Error | null }>
    }
    insert: (payload: Record<string, unknown>) => {
      select: () => {
        single: () => Promise<{ data: DbRow | null; error: Error | null }>
      }
    }
  }
}

export function mapDbOrganizationToOrganizacion(row: DbRow): Organizacion {
  return {
    id: asString(row.id),
    nombre: asString(row.name),
    nombre_legal: asString(row.legal_name),
    pais: asString(row.country),
    zona_horaria: asString(row.timezone, 'UTC'),
    creado_por: optionalString(row.created_by),
    creado_en: asString(row.created_at),
    actualizado_en: asString(row.updated_at),
  }
}

export function mapDbOrganizationMemberToMembresia(row: DbRow): MembresiaOrganizacion {
  return {
    organizacion_id: asString(row.organization_id),
    usuario_id: asString(row.user_id),
    rol: asRolOrganizacion(row.role),
    unido_en: asString(row.joined_at),
  }
}

export function crearPayloadOrganizacion(params: {
  usuarioId: string
  nombre: string
  nombreLegal?: string
  pais?: string
  zonaHoraria?: string
}): Record<string, unknown> {
  return {
    name: params.nombre.trim(),
    legal_name: params.nombreLegal?.trim() ?? '',
    country: params.pais?.trim().toUpperCase() ?? '',
    timezone: params.zonaHoraria?.trim() || 'UTC',
    created_by: params.usuarioId,
  }
}

export function resolveAutoSelectedOrganizationId(params: {
  currentOrganizationId?: string | null
  organizations: Pick<Organizacion, 'id'>[]
}): string | null {
  const visibleIds = new Set(params.organizations.map(org => org.id).filter(Boolean))
  if (params.currentOrganizationId) {
    return visibleIds.has(params.currentOrganizationId) ? params.currentOrganizationId : null
  }

  return params.organizations.length === 1 ? params.organizations[0].id : null
}

export async function obtenerOrganizaciones(
  client: OrganizationRepositoryClient = supabase as unknown as OrganizationRepositoryClient
): Promise<Organizacion[]> {
  const { data, error } = await client
    .from('organizations')
    .select('*')
    .order('created_at', { ascending: false })

  if (error) throw error
  return (data ?? []).map(mapDbOrganizationToOrganizacion)
}

export async function crearOrganizacionConOwner(params: {
  usuarioId: string
  nombre: string
  nombreLegal?: string
  pais?: string
  zonaHoraria?: string
  client?: OrganizationRepositoryClient
}): Promise<Organizacion> {
  const client = params.client ?? (supabase as unknown as OrganizationRepositoryClient)
  const { data, error } = await client
    .from('organizations')
    .insert(crearPayloadOrganizacion(params))
    .select()
    .single()

  if (error) throw error
  const organization = mapDbOrganizationToOrganizacion(data ?? {})

  const member = await client
    .from('organization_members')
    .insert({
      organization_id: organization.id,
      user_id: params.usuarioId,
      role: 'owner',
    })
    .select()
    .single()

  if (member.error) throw member.error
  return organization
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function asRolOrganizacion(value: unknown): RolOrganizacion {
  return value === 'admin' || value === 'radiologist' || value === 'staff' ? value : 'owner'
}
